import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Add, FilterList, ImportExport, RestartAlt } from "@mui/icons-material";
import {
  Alert,
  Button,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import {
  ActivationCard,
  ButtonIcon,
  CreateNewCard,
  FilterBox,
  SearchBox,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { useAudienceContext, useDialogs } from "@/context";
import { useActivations } from "@/hooks";
import { Activation, FilterOption } from "@/types";
import {
  activationFilterOptions,
  activationSortOptions,
  defaultActivation,
  filterActivations,
} from "@/utils";
import { ActivationListSkeleton } from "./ActivationListSkeleton";
import { shareActivation } from "@/api";
import { useAuth0 } from "@auth0/auth0-react";
import { SHARE_ACTIVATION } from "@/context/Audience";

const ActivationListPage: React.FC = () => {
  const { dispatch } = useAudienceContext();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialogs();
  const { getAccessTokenSilently } = useAuth0();
  const { activations, loading, error } = useActivations();
  const [rows, setRows] = useState<Activation[]>([]);

  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");
  const [statusList, setStatusList] = useState<FilterOption[]>([]);
  const [filterBy, setFilterBy] = useState<string>("");
  const [updateError, setUpdateError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setStatusList(activationFilterOptions);
    const filteredActivations = filterActivations(
      activations,
      searchText,
      filterBy,
      sortBy
    );

    if (filteredActivations) {
      setRows([defaultActivation, ...filteredActivations]);
    } else {
      setRows([defaultActivation]);
    }
  }, [activations, filterBy, searchText, sortBy]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSortSelect = (event: SelectChangeEvent) =>
    setSortBy(event.target.value as string);

  const handleFilterBySelect = (value: string) =>
    setFilterBy((prevState) => (prevState === value ? "" : value));

  const handleReset = () => {
    setSearchText("");
    setFilterBy("");
    setSortBy("");
  };

  const handleCreate = () => {
    dispatch({ type: "CREATE_ACTIVATION" });
    navigate("new");
  };

  const handleSelect = (activation: Activation) => {
    dispatch({ type: "GET_ACTIVATION", payload: activation });
    navigate(activation.campaign_id);
  };

  const handleShare = async (campaignId: string, ids: number[]) => {
    const accessToken = await getAccessTokenSilently();

    if (!campaignId) return;
    const { data, error } = await shareActivation(accessToken, campaignId, ids);

    if (data) {
      dispatch({
        type: SHARE_ACTIVATION,
        payload: { activationId: campaignId, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      setUpdateError(error.message);
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Add />}
        onClick={handleCreate}
      >
        Create New
      </Button>
    </Stack>
  );

  return (
    <PageContainer actions={actionButtons}>
      {updateError ? (
        <Grid>
          <Alert variant="filled" severity="error">
            {updateError}
          </Alert>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography>My Activations</Typography>
          </Grid>

          {/* Filters & Search bar */}
          <Grid container size={12} alignItems="center">
            <Grid>
              <SearchBox value={searchText} onSearch={handleSearch} />
            </Grid>
            <Grid>
              <FilterBox
                label="Status"
                value={filterBy}
                options={statusList}
                onClick={handleFilterBySelect}
                startIcon={<FilterList fontSize="small" />}
                showSelected
              />
            </Grid>
            <Grid>
              <FilterBox
                label="Sort"
                value={sortBy}
                options={activationSortOptions}
                onChange={handleSortSelect}
                startIcon={<ImportExport fontSize="small" />}
                showSelected
              />
            </Grid>
            <Grid>
              <ButtonIcon
                onClick={handleReset}
                color="info"
                size={38}
                Icon={RestartAlt}
                tooltip="Reset Filters"
              />
            </Grid>
          </Grid>

          <Grid container size={12} mb={4}>
            {loading ? (
              <ActivationListSkeleton />
            ) : error ? (
              <Grid>
                <Alert variant="filled" severity="error">
                  It was not possible to load your activations at this time,
                  please try again.
                </Alert>
              </Grid>
            ) : (
              rows.map((activation, index) => (
                <Grid key={index}>
                  {index === 0 ? (
                    <CreateNewCard
                      title="Create New Activation"
                      onClick={handleCreate}
                    />
                  ) : (
                    <ActivationCard
                      activation={activation}
                      onEdit={handleSelect}
                      onShare={() =>
                        openDialog({
                          type: "shareActivation",
                          props: {
                            selectedEntity: activation,
                            onShare: async (ids: number[]) => {
                              await handleShare(activation.campaign_id, ids);
                            },
                          },
                        })
                      }
                    />
                  )}
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
      )}
    </PageContainer>
  );
};

export default ActivationListPage;
